import SearchIcon from '@mui/icons-material/Search';
import { Box, CircularProgress, Container, Grid, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { getSydneyDateTime, WO2ToggleSwitch } from 'src/common';
import { Props } from '../container';
import { buildDescriptionSearchPagination } from '../store/slice';
import { CashStatementLine, CashStatementCsvRow } from '../store/types';
import { CashAccountsTable } from './cashAccountsTable';
import AccountSelect from '../../transactions/components/accountSelect';
import DateRangePicker from 'src/common/components/DateRangePicker';
import WO2Button from 'src/common/components/button/Button';
import { PagedRequest } from 'src/store';

export const CashAccounts = (props: Props): JSX.Element => {
  const {
    parameters,
    clientId,
    showAudValues,
    canShowAudValues,
    lastDataRefresh,
    selectedAccountLabel,
    setAccountParameter,
    setDateParameter,
    setPaginationParameter,
    setShowAudValues,
    fetchCashAccounts,
    fetchCashStatements,
    downloadCashStatements,
    progressCashStatements,
    progressCashAccounts,
  } = props;

  const [initialSearchComplete, setInitialSearchComplete] = useState(false);
  const [searchText, setSearchText] = useState('');

  const handleSearchClick = () => {
    const pagination = buildDescriptionSearchPagination(parameters.pagination, searchText);
    setPaginationParameter(pagination);
    if (!!clientId) {
      fetchCashStatements({
        inputs: { clientId, parameters: { ...parameters, pagination } },
        pagination,
      });
    }
  };

  const fetchHandleSearchClick = useCallback(() => {
    if (!!clientId) {
      fetchCashStatements({
        inputs: { clientId, parameters },
        pagination: parameters.pagination,
      });
    }
  }, [fetchCashStatements, clientId, parameters]);

  useEffect(() => {
    if (!!clientId) {
      fetchCashAccounts(clientId);
    }
  }, [fetchCashAccounts, clientId]);

  useEffect(() => {
    if (parameters.accountIdList.length > 0 && !initialSearchComplete) {
      fetchHandleSearchClick();
      setInitialSearchComplete(true);
    }
  }, [parameters, fetchHandleSearchClick, initialSearchComplete]);

  return (
    <Container style={{ paddingTop: '20px', paddingBottom: '20px' }}>
      <Grid container spacing={2} alignItems="center" justifyContent="flex-end">
        <Grid item>
          <Grid container spacing={2} alignItems="center">
            {canShowAudValues && (
              <Grid item>
                <Box display="flex" alignItems="center" gap={1}>
                  <WO2ToggleSwitch onChange={(e) => setShowAudValues(e.target.checked)} checked={showAudValues} color="primary" />
                  <Typography variant="h4">Show AUD values</Typography>
                </Box>
              </Grid>
            )}
            <Grid item>
              {progressCashAccounts.isLoading ? (
                <CircularProgress />
              ) : (
                <AccountSelect items={props.cashAccounts} selectedAccountId={props.parameters.selectedAccountId} setAccount={setAccountParameter} />
              )}
            </Grid>
            <Grid item>
              <DateRangePicker dateRange={props.parameters.dates} setDateRange={setDateParameter} isDateOnly={false} paddingRight={0} maxRangeDays={90} />
            </Grid>
            <Grid item>
              <TextField
                variant="outlined"
                placeholder="Search description..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchClick();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item>
              <WO2Button color={'primary'} disableElevation onClick={handleSearchClick} variant={'contained'} value="Search">
                Search
              </WO2Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {!!lastDataRefresh && (
        <Grid container justifyContent="flex-end" style={{ paddingTop: '10px' }}>
          <Grid item>
            <Tooltip title="Cash account data is refreshed hourly, at 45 minutes past the hour. Any activity since this time will not appear until the next refresh.">
              <Typography variant="body2" color="textSecondary">
                Last refreshed: {getSydneyDateTime(lastDataRefresh)}
              </Typography>
            </Tooltip>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2} style={{ paddingTop: '20px', paddingBottom: '20px' }}>
        <Grid item xs={12}>
          <CashAccountsTable
            pagedRequest={props.parameters.pagination}
            data={props.cashStatements?.results ?? []}
            accountLabel={selectedAccountLabel}
            showAudValues={showAudValues}
            progressCashStatements={progressCashStatements}
            handleDownload={(pagedRequest: PagedRequest, mapper: (lines: CashStatementLine[]) => CashStatementCsvRow[]) => {
              if (!!clientId) {
                downloadCashStatements({
                  inputs: { clientId, parameters },
                  pagination: pagedRequest,
                  mapper,
                });
              }
            }}
          />
        </Grid>
      </Grid>
    </Container>
  );
};
